import React, { createContext, useContext, useState, useEffect } from 'react';

export const THEMES = [
  {
    id: 'light',
    name: 'Light Default',
    description: 'Crisp light interface with subtle slate and blue highlights',
    isDark: false,
    monacoTheme: 'vs',
    styles: {
      appBg: 'min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 text-slate-900 transition-colors duration-300',
      headerBg: 'bg-white/80 border-slate-200 backdrop-blur-md',
      cardBg: 'bg-white/90 border-slate-200 text-slate-900 backdrop-blur-sm shadow-sm',
      textPrimary: 'text-slate-900',
      textMuted: 'text-slate-500',
      badgeBg: 'bg-blue-50 border-blue-200 text-blue-900',
      accentColor: 'bg-blue-600 hover:bg-blue-700 text-white',
      border: 'border-slate-200'
    },
    preview: {
      bg: 'bg-slate-100',
      header: 'bg-white',
      accent: 'bg-blue-600',
      text: 'text-slate-900',
      border: 'border-slate-200'
    }
  },
  {
    id: 'dark',
    name: 'Dark Slate',
    description: 'Sleek dark mode with deep slate background and indigo accents',
    isDark: true,
    monacoTheme: 'vs-dark',
    styles: {
      appBg: 'min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100 transition-colors duration-300',
      headerBg: 'bg-slate-900/90 border-slate-800 backdrop-blur-md',
      cardBg: 'bg-slate-900/90 border-slate-800 text-slate-100 backdrop-blur-sm shadow-md',
      textPrimary: 'text-slate-100',
      textMuted: 'text-slate-400',
      badgeBg: 'bg-indigo-950/60 border-indigo-800/60 text-indigo-300',
      accentColor: 'bg-indigo-600 hover:bg-indigo-700 text-white',
      border: 'border-slate-800'
    },
    preview: {
      bg: 'bg-slate-950',
      header: 'bg-slate-900',
      accent: 'bg-indigo-500',
      text: 'text-slate-100',
      border: 'border-slate-800'
    }
  },
  {
    id: 'cyberpunk',
    name: 'Cyberpunk Neon',
    description: 'Futuristic high-contrast dark theme with neon cyan & magenta',
    isDark: true,
    monacoTheme: 'vs-dark',
    styles: {
      appBg: 'min-h-screen bg-gradient-to-br from-zinc-950 via-purple-950/40 to-zinc-950 text-cyan-50 transition-colors duration-300',
      headerBg: 'bg-zinc-900/90 border-cyan-500/30 backdrop-blur-md',
      cardBg: 'bg-zinc-900/90 border-cyan-500/30 text-cyan-50 backdrop-blur-sm shadow-cyan-950/50 shadow-md',
      textPrimary: 'text-cyan-100',
      textMuted: 'text-purple-300/70',
      badgeBg: 'bg-cyan-950/80 border-cyan-500/40 text-cyan-300',
      accentColor: 'bg-cyan-500 hover:bg-cyan-400 text-zinc-950 font-semibold',
      border: 'border-cyan-500/30'
    },
    preview: {
      bg: 'bg-zinc-950',
      header: 'bg-zinc-900',
      accent: 'bg-cyan-400',
      text: 'text-cyan-100',
      border: 'border-cyan-500/40'
    }
  },
  {
    id: 'emerald',
    name: 'Emerald Mint',
    description: 'Calming dark green background with vibrant mint emerald accents',
    isDark: true,
    monacoTheme: 'vs-dark',
    styles: {
      appBg: 'min-h-screen bg-gradient-to-br from-emerald-950 via-slate-950 to-emerald-950 text-emerald-50 transition-colors duration-300',
      headerBg: 'bg-emerald-950/90 border-emerald-800/60 backdrop-blur-md',
      cardBg: 'bg-emerald-950/80 border-emerald-800/60 text-emerald-50 backdrop-blur-sm shadow-emerald-950/40 shadow-md',
      textPrimary: 'text-emerald-50',
      textMuted: 'text-emerald-300/70',
      badgeBg: 'bg-emerald-900/60 border-emerald-700/60 text-emerald-300',
      accentColor: 'bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-semibold',
      border: 'border-emerald-800/60'
    },
    preview: {
      bg: 'bg-emerald-950',
      header: 'bg-emerald-900',
      accent: 'bg-emerald-400',
      text: 'text-emerald-50',
      border: 'border-emerald-800'
    }
  },
  {
    id: 'sapphire',
    name: 'Sapphire Night',
    description: 'Deep navy blue dark theme with sapphire blue highlights',
    isDark: true,
    monacoTheme: 'vs-dark',
    styles: {
      appBg: 'min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 text-blue-50 transition-colors duration-300',
      headerBg: 'bg-blue-950/90 border-blue-800/60 backdrop-blur-md',
      cardBg: 'bg-blue-950/80 border-blue-800/60 text-blue-50 backdrop-blur-sm shadow-blue-950/50 shadow-md',
      textPrimary: 'text-blue-50',
      textMuted: 'text-blue-300/70',
      badgeBg: 'bg-blue-900/60 border-blue-700/60 text-blue-300',
      accentColor: 'bg-blue-600 hover:bg-blue-500 text-white',
      border: 'border-blue-800/60'
    },
    preview: {
      bg: 'bg-slate-950',
      header: 'bg-blue-950',
      accent: 'bg-blue-500',
      text: 'text-blue-50',
      border: 'border-blue-800'
    }
  },
  {
    id: 'sunset',
    name: 'Sunset Amber',
    description: 'Rich dark warm theme with glowing amber and rose accents',
    isDark: true,
    monacoTheme: 'vs-dark',
    styles: {
      appBg: 'min-h-screen bg-gradient-to-br from-stone-950 via-amber-950/30 to-stone-950 text-amber-50 transition-colors duration-300',
      headerBg: 'bg-stone-900/90 border-amber-900/40 backdrop-blur-md',
      cardBg: 'bg-stone-900/90 border-amber-900/40 text-amber-50 backdrop-blur-sm shadow-amber-950/30 shadow-md',
      textPrimary: 'text-amber-50',
      textMuted: 'text-amber-300/70',
      badgeBg: 'bg-amber-950/60 border-amber-800/60 text-amber-300',
      accentColor: 'bg-amber-500 hover:bg-amber-600 text-stone-950 font-semibold',
      border: 'border-amber-900/40'
    },
    preview: {
      bg: 'bg-stone-950',
      header: 'bg-stone-900',
      accent: 'bg-amber-500',
      text: 'text-amber-100',
      border: 'border-amber-900/50'
    }
  }
];

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    const savedTheme = localStorage.getItem('datapilot-theme');
    return savedTheme || 'light';
  });

  const activeThemeObj = THEMES.find((t) => t.id === theme) || THEMES[0];

  const setTheme = (newThemeId) => {
    setThemeState(newThemeId);
    localStorage.setItem('datapilot-theme', newThemeId);
  };

  useEffect(() => {
    // Set data-theme on root element
    document.documentElement.setAttribute('data-theme', theme);
    if (activeThemeObj.isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme, activeThemeObj]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, activeThemeObj, themes: THEMES }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
